import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Box, Alert, Snackbar, Chip, Tooltip, Typography } from '@mui/material';
import { SettingsInputComponent, SettingsInputComponentOutlined, RadioButtonChecked, RadioButtonUnchecked } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { selectDealerSelected } from '../../redux/headerSlice/selectors';
import SplitPaneMAIQ from '../SplitPaneMAIQ';
import OperationsPanel from './OperationsPanel';
import CommonInterface from './HomePageInterface';
import ChatPanel from './ChatPanel';
import CleanChatPanel from './CleanChatPanel';
import EmailContentPreview from './EmailContent/EmailContentPreview';
import EmailPreviewModal from '../EmailPreviewModal';
import { Canvas } from './Canvas';
import { CanvasType } from './Canvas/types';
import { GenerationProgressPopupMAIQ } from 'app/components/GenerationProgressPopupMAIQ';
import { 
  ChatState, 
  FlowStep, 
  GenerateFlowProps,
} from '../../types';
import { useMiaChatV2 } from '../../globalContexts/MiaChatContext';
import { Suggestion } from 'app/NewAIAvatarLayout/services/apiService/miaChat/types';
import { Message } from '../../types';
import MiaChatApiService from 'app/NewAIAvatarLayout/services/apiService/miaChat/miaChatApiService';
import { miaChatV2SocketService, MiaChatEvent } from 'app/NewAIAvatarLayout/services/socketService/miaChat/miaChatSocketService';
import { PromptSelection } from './OperationsPanel/types';
import { PageHeader } from '../StyledComponents';

interface MiaChatProps extends GenerateFlowProps {
  interfaceType?: 'homepage' | 'generate' | 'default' | 'analyze' | 'target';
}

// Move initialChatState outside component to prevent recreation on every render
const createInitialChatState = (): ChatState => ({
  step: 0,
  chatHistory: [],
  emailContent: { subject: '', sections: [] },
  activeTab: "Generate",
  showPreview: false,
  inputText: "",
  loading: false,
  error: null,
  suggestions: [],
  next_action: '',
  is_input_restricted_to_suggestions: false
});

const MiaChat: React.FC<MiaChatProps> = ({ dealerId, interfaceType = 'default' }) => {
  const selectedDealer = useSelector(selectDealerSelected);
  
  // Use MiaChatV2Context instead of local state
  const {
    loading,
    setLoading,
    error,
    setError,
    currentConversation,
    setCurrentConversation,
    initChat,
    startNewChat,
    sendChatMessage,
    updateConversationState,
    operations,
    segments,
    getSegmentsAsOperations,
    fetchOperations,
    loading: operationsLoading
  } = useMiaChatV2();
  
  const currentDealerId = dealerId || selectedDealer?.dealer || '';
  
  // Helper function to get selected_operation based on interfaceType
  const getSelectedOperation = useCallback((): string | undefined => {
    if (interfaceType === 'analyze') {
      return 'DATA_ANALYTICS';
    } else if (interfaceType === 'target') {
      return 'TARGET_BUYER_PROFILE';
    }
    return undefined;
  }, [interfaceType]);
  
  // Use useMemo to create initialChatState only once
  const initialChatState = useMemo(() => createInitialChatState(), []);

  // State for selected filters
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);

  // Chat state management
  const [chatState, setChatState] = useState<ChatState>(initialChatState);

  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showEditableEmail, setShowEditableEmail] = useState(false);
  const [generatedEmail, setGeneratedEmail] = useState<{
    generatedHtml: string;
    subject: string;
    conversation_id: string;
    emailPreview?: {
      subject: string;
      sections: any[];
      meta: {
        name: string;
        subject: string;
        'template type'?: string;
        template_type?: string;
      };
    };
  } | null>(null);
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);
  const [showEmailGenerationPopup, setShowEmailGenerationPopup] = useState(true); // Control popup visibility
  
  // State to track if chat inputs should be disabled (during email generation and template save + PUT API call)
  const [isChatInputDisabled, setIsChatInputDisabled] = useState(false);
  
  // State for email preview modal
  const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false);

  // Streaming data state for GenerationProgressPopupMAIQ component
  const [streamingData, setStreamingData] = useState<any>(null);
  const [streamingText, setStreamingText] = useState<string>('');
  const [currentSection, setCurrentSection] = useState<{index: number; type: string; main?: string; sub_text?: string} | null>(null);

  // Chat history management
  const [chatHistory, setChatHistory] = useState<Array<{
    id: string;
    title: string;
    timestamp: Date;
    type: 'email' | 'general' | 'template';
  }>>([]);
  
  const [activeConversationId, setActiveConversationId] = useState<string>('');
  const [chatStates, setChatStates] = useState<Record<string, ChatState>>({});

  const INTERRUPTION_ASSISTANT_CONTENT = 'Looks like something went wrong just now. Please start a new chat and try again.';
  const INTERRUPTION_ERROR_MESSAGE = 'Chat interrupted due to an unexpected issue. Please start a new chat.';

  const appendInterruptionMessage = useCallback((messages: Message[] = []) => {
    const hasInterruptionMessage = messages.some(
      (msg) => msg.role === 'assistant' && msg.content === INTERRUPTION_ASSISTANT_CONTENT
    );

    if (hasInterruptionMessage) {
      return messages;
    }

    return [
      ...messages,
      {
        role: 'assistant' as const,
        content: INTERRUPTION_ASSISTANT_CONTENT,
      },
    ];
  }, [INTERRUPTION_ASSISTANT_CONTENT]);

  const handleChatInterruption = useCallback(
    (messagesOverride?: Message[], errorMessage?: string) => {
      let updatedStateRef: ChatState | null = null;

      setChatState((prev) => {
        const baseMessages =
          messagesOverride && messagesOverride.length > 0 ? messagesOverride : prev.chatHistory;
        const chatHistoryWithInterruption = appendInterruptionMessage(baseMessages);

        updatedStateRef = {
          ...prev,
          chatHistory: chatHistoryWithInterruption,
          loading: false,
          error: errorMessage || INTERRUPTION_ERROR_MESSAGE,
          suggestions: [],
          is_input_restricted_to_suggestions: false,
        };

        return updatedStateRef;
      });

      setLoading(false);
      setError(errorMessage || INTERRUPTION_ERROR_MESSAGE);

      if (activeConversationId && updatedStateRef) {
        setChatStates((prev) => ({
          ...prev,
          [activeConversationId]: updatedStateRef as ChatState,
        }));
      }
    },
    [appendInterruptionMessage, setLoading, setError, activeConversationId, setChatStates, INTERRUPTION_ERROR_MESSAGE]
  );

  // Socket service refs and state
  const socketServiceRef = useRef<any>(null);
  const [isSocketConnected, setIsSocketConnected] = useState<boolean>(false);
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  
  // Auto-resubscribe logic: Track intentional unsubscribes
  const intentionalUnsubscribeRef = useRef<boolean>(false);
  const resubscribeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastResubscribeAttemptRef = useRef<number>(0);
  const isResubscribingRef = useRef<boolean>(false); // Track if resubscribe is in progress
  const lastResubscribedConversationIdRef = useRef<string | null>(null); // Track which conversation was last resubscribed
  
  // API call tracking state - to control when socket messages should be shown
  const [isApiCallInProgress, setIsApiCallInProgress] = useState<boolean>(false);
  
  // AbortController for all chat-related API calls
  const emailGenerationAbortControllerRef = useRef<AbortController | null>(null);
  const initChatAbortControllerRef = useRef<AbortController | null>(null);
  const startNewChatAbortControllerRef = useRef<AbortController | null>(null);
  const sendChatMessageAbortControllerRef = useRef<AbortController | null>(null);
  const updateConversationStateAbortControllerRef = useRef<AbortController | null>(null);

  // Helper function to transform operations to suggestions
  // Use useMemo instead of useCallback for computed values
  const getOperationsAsSuggestions = useMemo(() => {
    // Return empty array if no operations - skeleton will be shown instead
    if (!operations || operations.length === 0) {
      return [];
    }
    
    // Transform operations to suggestions (use display_text field)
    return operations.map(operation => operation.display_text || 'Operation');
  }, [operations]);

  // Helper function to find operation by display_text
  const findOperationByTitle = useCallback((displayText: string) => {
    if (!operations || operations.length === 0) return null;
    
    return operations.find(operation => 
      operation.display_text === displayText
    );
  }, [operations]);

  // Fetch operations for generate and target interfaces
  useEffect(() => {
    if (interfaceType === 'generate' || interfaceType === 'target') {
      fetchOperations();
    }
  }, [interfaceType, fetchOperations]); // Now safe to include fetchOperations as it's memoized

  // Initialize socket service
  useEffect(() => {
    try {
      const wsUrl = process.env.REACT_APP_WS_URL;
      if (wsUrl) {
        const config = {
          url: wsUrl,
          autoConnect: true,
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          options: {
            path: '/ws/',
            transports: ['websocket', 'polling']
          }
        };
        
        socketServiceRef.current = miaChatV2SocketService(config);
        socketServiceRef.current.connect();
        setIsSocketConnected(socketServiceRef.current.isConnected());
        
        console.log('🔌 MiaChatV2 Socket Service initialized');
      } else {
        console.warn('⚠️ WebSocket URL not configured');
      }
    } catch (error) {
      console.error('❌ Failed to initialize socket service:', error);
    }

    return () => {
      // Mark as intentional unsubscribe (page refresh/unmount)
      intentionalUnsubscribeRef.current = true;
      
      // Clear any pending resubscribe attempts
      if (resubscribeTimeoutRef.current) {
        clearTimeout(resubscribeTimeoutRef.current);
        resubscribeTimeoutRef.current = null;
      }
      
      // Cancel ALL ongoing API calls on component unmount
      const abortControllers = [
        { ref: emailGenerationAbortControllerRef, name: 'email generation' },
        { ref: initChatAbortControllerRef, name: 'init chat' },
        { ref: startNewChatAbortControllerRef, name: 'start new chat' },
        { ref: sendChatMessageAbortControllerRef, name: 'send chat message' },
        { ref: updateConversationStateAbortControllerRef, name: 'update conversation state' }
      ];
      
      abortControllers.forEach(({ ref, name }) => {
        if (ref.current) {
          ref.current.abort();
          ref.current = null;
        }
      });
      
      // CRITICAL: Update refs before checking to ensure latest values
      const isEmailGenInProgress = isGeneratingEmailRef.current;
      const emailGenConversationId = emailGenerationConversationIdRef.current;
      const latestActiveConversationId = activeConversationIdRef.current;
      
      if (socketServiceRef.current) {
        // On page refresh/unmount, we still need to disconnect
        // But check if email generation is in progress for the active conversation
        if (latestActiveConversationId) {
          // Only unsubscribe if email generation is not in progress for this conversation
          if (!isEmailGenInProgress || emailGenConversationId !== latestActiveConversationId) {
            socketServiceRef.current.unsubscribeFromConversation(latestActiveConversationId);
          } else {
            console.log('⚠️ Skipping unsubscribe on unmount - email generation in progress');
          }
        }
        socketServiceRef.current.disconnect();
      }
    };
  }, []);

  // Socket event handler - use refs to avoid dependency issues
  const activeConversationIdRef = useRef(activeConversationId);
  const isApiCallInProgressRef = useRef(isApiCallInProgress);
  const isGeneratingEmailRef = useRef<boolean>(false);
  const emailGenerationConversationIdRef = useRef<string | null>(null); // Track which conversation is generating email
  
  // Update refs when values change
  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);
  
  useEffect(() => {
    isApiCallInProgressRef.current = isApiCallInProgress;
  }, [isApiCallInProgress]);
  
  // Update email generation ref when state changes
  useEffect(() => {
    isGeneratingEmailRef.current = isGeneratingEmail;
    // Clear conversation ID ref when email generation stops
    if (!isGeneratingEmail) {
      emailGenerationConversationIdRef.current = null;
    }
  }, [isGeneratingEmail]);

  const handleSocketEvent = useCallback((eventData: MiaChatEvent) => {
    // CRITICAL: For processing_status events, always show them if we have an active conversation
    // Don't block them based on isApiCallInProgress flag
    const isProcessingStatus = eventData.message?.processing_type || 
                                (eventData as any).processing_type ||
                                eventData.tool_name;
    
    const hasActiveConversation = activeConversationIdRef.current && 
                                   eventData.conversation_id === activeConversationIdRef.current;
    
    // Allow processing_status events through even if API call not in progress
    // This ensures events show immediately after resubscribe
    if (!isApiCallInProgressRef.current && !isProcessingStatus) {
      return;
    }
    
    // If it's a processing_status event and we have active conversation, ensure API call flag is set
    if (isProcessingStatus && hasActiveConversation && !isApiCallInProgressRef.current) {
      setIsApiCallInProgress(true);
    }

    // Handle processing_status events with tool call processing_type
    const message = eventData.message || (eventData as any).message;
    const processingType = message?.processing_type;
    
    if (message && processingType) {
      if (processingType) {
        const _message: Message = {
          role: 'system',
          content: message.text,
          id: `_message_${Date.now()}_${Math.random()}`,
          timestamp: eventData.timestamp || new Date().toISOString(),
          bgcolor: processingType.includes('completed') ? '#d4edda' : '#fff3cd', // Yellow for executing, green for completed
          processingType: processingType
        };

        // Add message to chat history (avoid duplicates)
        setChatState(prev => {
          // Check if message with same content already exists
          const exists = prev.chatHistory.some(msg => 
            msg.role === 'system' && 
            msg.content === _message.content && 
            msg.processingType === _message.processingType
          );
          
          if (exists) return prev;
          
          return {
            ...prev,
            chatHistory: [...prev.chatHistory, _message]
          };
        });

        // Also update context conversation if available
        setCurrentConversation(prevConversation => {
          if (!prevConversation) {
            return prevConversation;
          }

          return {
            ...prevConversation,
            messages: [...(prevConversation.messages || []), _message]
          };
        });
      }
    }

    // Handle direct tool call events (keeping for backward compatibility)
    if (eventData.tool_name) {
      const _message: Message = {
        role: 'system',
        content: eventData.message?.content || '',
        id: `tool_${Date.now()}_${Math.random()}`,
        timestamp: eventData.timestamp || new Date().toISOString(),
        bgcolor: eventData.tool_name.includes('completed') ? '#d4edda' : '#fff3cd', // Yellow for executing, green for completed
        processingType: eventData.processing_type || 'processing'
      };

      // Add tool message to chat history (avoid duplicates)
      setChatState(prev => {
        // Check if message with same content already exists
        const exists = prev.chatHistory.some(msg => 
          msg.role === 'system' && 
          msg.content === _message.content && 
          msg.processingType === _message.processingType
        );
        
        if (exists) return prev;
        
        return {
          ...prev,
          chatHistory: [...prev.chatHistory, _message]
        };
      });

      // Also update context conversation if available - use functional update with duplicate check
      setCurrentConversation(prevConversation => {
        if (!prevConversation) {
          return prevConversation;
        }
        
        // Check for duplicates before updating
        const exists = prevConversation.messages?.some(msg => 
          msg.role === 'system' && 
          msg.content === _message.content && 
          (msg as any).processingType === _message.processingType
        );
        
        if (exists) return prevConversation;

        return {
          ...prevConversation,
          messages: [...(prevConversation.messages || []), _message]
        };
      });
    }

    // Handle streaming section response data
    if (eventData.message && (eventData.message as any).sections && (eventData.message as any).text) {
      setStreamingData(eventData);
      
      // Update current section if available
      if ((eventData.message as any).sections && (eventData.message as any).sections.length > 0) {
        const currentSectionData = (eventData.message as any).sections.find((section: any) => 
          section.index === (eventData.message as any).text?.key?.index
        );
        if (currentSectionData) {
          setCurrentSection({
            index: currentSectionData.index,
            type: currentSectionData.type,
            main: currentSectionData.main || '',
            sub_text: currentSectionData.sub_text || ''
          });
        }
      }
    }
  }, []); // Remove dependencies to prevent callback recreation

  // Subscribe to conversation when activeConversationId changes
  useEffect(() => {
    const currentConversationId = activeConversationId;
    
    if (socketServiceRef.current && currentConversationId) {
      // Clear intentional unsubscribe flag when subscribing to new conversation
      intentionalUnsubscribeRef.current = false;
      
      socketServiceRef.current.subscribeToConversation(
        currentConversationId,
        handleSocketEvent
      );
      // Update subscription status immediately
      setIsSubscribed(true);
    } else {
      setIsSubscribed(false);
    }

    return () => {
      // CRITICAL: Use refs to get latest values, not closure values
      const isEmailGenInProgress = isGeneratingEmailRef.current;
      const emailGenConversationId = emailGenerationConversationIdRef.current;
      const latestActiveConversationId = activeConversationIdRef.current;
      
      // Prevent unsubscribe if:
      // 1. Email generation is in progress
      // 2. AND it's for the same conversation we're trying to unsubscribe from
      if (isEmailGenInProgress && emailGenConversationId === currentConversationId) {
        console.log('⚠️ Skipping unsubscribe - email generation in progress for conversation:', currentConversationId);
        return;
      }
      
      // Only unsubscribe if:
      // 1. We have a valid conversation ID
      // 2. It matches the current active conversation (not stale)
      // 3. Email generation is not in progress for this conversation
      // 4. It's an intentional unsubscribe (not unexpected)
      if (socketServiceRef.current && currentConversationId && currentConversationId === latestActiveConversationId) {
        // Double check email generation status before unsubscribing
        if (!isGeneratingEmailRef.current || emailGenerationConversationIdRef.current !== currentConversationId) {
          // Only unsubscribe if it's intentional (reset, dealer change, etc.)
          // If not intentional, the auto-resubscribe logic will handle it
          if (intentionalUnsubscribeRef.current) {
            socketServiceRef.current.unsubscribeFromConversation(currentConversationId);
            setIsSubscribed(false);
            // Reset the flag after intentional unsubscribe
            intentionalUnsubscribeRef.current = false;
          }
        }
      }
    };
  }, [activeConversationId]); // Remove handleSocketEvent dependency

  // Update chat state safely and sync with context
  // Removed chatState from dependencies to prevent unnecessary recreations
  const updateChatState = useCallback((updates: Partial<ChatState>) => {
    setChatState(prev => {
      const newState = { ...prev, ...updates };
      return newState;
    });
    
    // Sync loading state with context
    if (updates.loading !== undefined) {
      setLoading(updates.loading);
    }
    
    // Sync error state with context
    if (updates.error !== undefined) {
      setError(updates.error || '');
    }
  }, [setLoading, setError]);

  // Comprehensive reset function for all chat states
  const resetAllChatStates = useCallback(() => {
    console.log('🔄 Resetting all chat states...');
    
    // Mark this as intentional unsubscribe (reset action)
    intentionalUnsubscribeRef.current = true;
    
    // Clear any pending resubscribe attempts
    if (resubscribeTimeoutRef.current) {
      clearTimeout(resubscribeTimeoutRef.current);
      resubscribeTimeoutRef.current = null;
    }
    
    // CRITICAL: Check if email generation is in progress before unsubscribing
    const isEmailGenInProgress = isGeneratingEmailRef.current;
    const emailGenConversationId = emailGenerationConversationIdRef.current;
    
    // Cancel ALL ongoing API calls
    const abortControllers = [
      { ref: emailGenerationAbortControllerRef, name: 'email generation' },
      { ref: initChatAbortControllerRef, name: 'init chat' },
      { ref: startNewChatAbortControllerRef, name: 'start new chat' },
      { ref: sendChatMessageAbortControllerRef, name: 'send chat message' },
      { ref: updateConversationStateAbortControllerRef, name: 'update conversation state' }
    ];
    
    abortControllers.forEach(({ ref, name }) => {
      if (ref.current) {
        console.log(`🛑 Cancelling ${name} API call`);
        ref.current.abort();
        ref.current = null;
      }
    });
    
    // CRITICAL: Update refs after aborting
    isGeneratingEmailRef.current = false;
    emailGenerationConversationIdRef.current = null;
    
    // Unsubscribe from current conversation ONLY if email generation is not in progress
    if (socketServiceRef.current && activeConversationId) {
      if (isEmailGenInProgress && emailGenConversationId === activeConversationId) {
        console.log('⚠️ Skipping unsubscribe in resetAllChatStates - email generation was in progress');
        // Note: After aborting above, email generation is cancelled, so socket disconnect will handle cleanup
      } else {
        console.log('🔌 Unsubscribing from conversation:', activeConversationId);
        socketServiceRef.current.unsubscribeFromConversation(activeConversationId);
        setIsSubscribed(false);
      }
    }
    
    // Reset chat state to initial state
    const resetState = {
      ...initialChatState,
      // For homepage and generate interfaces, ensure step is 0 to show interface
      step: interfaceType === 'homepage' || interfaceType === 'generate' ? 0 : initialChatState.step
    };
    
    setChatState(resetState);
    
    // Reset all other states
    setSelectedFilters([]);
    setSuccessMessage('');
    setIsGeneratingEmail(false);
    setShowEmailGenerationPopup(false);
    setGeneratedEmail(null);
    setShowEditableEmail(false);
    setIsChatInputDisabled(false);
    setShowEmailPreviewModal(false);
    
    // Reset streaming data states
    setStreamingData(null);
    setStreamingText('');
    setCurrentSection(null);
    
    // Reset API call tracking state
    setIsApiCallInProgress(false);
    
    // Reset conversation states
    setChatStates({});
    setActiveConversationId('');
    
    // Reset context states
    setLoading(false);
    setError('');
    
    // Reset current conversation context
    setCurrentConversation(null);
    
    console.log('✅ All chat states reset successfully');
  }, [initialChatState, interfaceType, activeConversationId, currentConversation, setLoading, setError, setCurrentConversation]);

  // Handle reset function for chat panels
  const handleReset = useCallback(() => {
    resetAllChatStates();
  }, [resetAllChatStates]);

  // Function to detect if email generation is ready
  const isEmailGenerationReady = useCallback((messages: Message[], next_action, terminal_action) => {
    if (!messages || messages.length === 0) return false;
    
    const lastMessage = messages[messages.length - 1];
    if (lastMessage.role !== 'assistant') return false;
    
    const content = lastMessage.content.toLowerCase();
    const isReady = (next_action === 'GENERATE_EMAIL') || content.includes('generate email');
    
    return isReady;
  }, []);

  // Function to call generateEmail API
  const generateEmailFromConversation = useCallback(async (messages: Message[], conversationId: string) => {
    try {
      if (!currentDealerId || !conversationId) {
        return;
      }

      // Create new AbortController for this email generation
      const abortController = new AbortController();
      emailGenerationAbortControllerRef.current = abortController;
      
      // CRITICAL: Update refs FIRST before state to prevent race conditions
      // This ensures cleanup functions see the correct value immediately
      isGeneratingEmailRef.current = true;
      emailGenerationConversationIdRef.current = conversationId;
      
      // Set email generation state to true AND disable chat inputs
      setIsGeneratingEmail(true);
      setShowEmailGenerationPopup(true); // Show popup when email generation starts
      setIsChatInputDisabled(true);
      
      const response = await MiaChatApiService.generateEmail({
        dealer_id: currentDealerId,
        conversation_id: conversationId,
        messages: messages,
        action: 'GENERATE_EMAIL'
      }, abortController.signal);

      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }

      if (response.body.generatedHtml) {
        // Debug logging to check API response structure
        console.log('📧 API Response Body:', response.body);
        console.log('📧 EmailPreview Data:', response.body.emailPreview);
        
        setGeneratedEmail({
          generatedHtml: response.body.generatedHtml,
          subject: response.body.subject,
          conversation_id: response.body.conversation_id,
          emailPreview: response.body.emailPreview
        });
        setSuccessMessage('Email generated successfully! Save as template to continue.');
        
        // Update chat panel with messages and suggestions from email generation response
        if (response.body.messages && Array.isArray(response.body.messages)) {
          const updatedMessages = response.body.messages.map((msg: any) => ({
            role: msg.role,
            content: msg.content,
            id: `email_gen_${Date.now()}_${Math.random()}`,
            timestamp: new Date().toISOString()
          }));
          
          // Update chat state with new messages and suggestions
          console.log('📧 Email API suggestions:', response.body.suggestions);
          updateChatState({
            chatHistory: updatedMessages,
            suggestions: response.body.suggestions || []
          });
          
          // Also update context conversation if available
          setCurrentConversation(prevConversation => {
            if (!prevConversation) {
              return prevConversation;
            }

            return {
              ...prevConversation,
              messages: updatedMessages
            };
          });
          
          // Save state for this conversation
          setChatStates(prev => ({
            ...prev,
            [conversationId]: {
              ...prev[conversationId],
              chatHistory: updatedMessages,
              suggestions: response.body.suggestions || []
            }
          }));
          
        }
      }
    } catch (error) {
      // Check if error is due to abort
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      
      console.error('Error generating email:', error);
      const contextError = error instanceof Error ? error.message : 'Failed to generate email. Please try again.';
      updateChatState({ error: contextError });
    } finally {
      // CRITICAL: Update refs FIRST before state to prevent race conditions
      isGeneratingEmailRef.current = false;
      emailGenerationConversationIdRef.current = null;
      
      // Always set email generation state to false and re-enable chat inputs when done
      setIsGeneratingEmail(false);
      setShowEmailGenerationPopup(false); // Hide popup when email generation completes
      setIsChatInputDisabled(false);
      // Clear the abort controller reference
      emailGenerationAbortControllerRef.current = null;
    }
  }, [currentDealerId, updateChatState, setCurrentConversation]);

  const handleOperationSelect = useCallback(async (prompt: PromptSelection) => {
    let pendingMessages: Message[] = [];
    try {
      const operation = prompt.action || prompt.title; //prefer the action over title if button is clicked
      const title = prompt.title;
      // Create immediate user message for selected operation
      const userMessage = { 
        role: "user" as const, 
        content: title,
        id: `user_${Date.now()}_${Math.random()}`,
        timestamp: new Date().toISOString()
      };

      pendingMessages = [userMessage];

      // Immediately update state to show chat panel with user message
      const immediateState: ChatState = {
        step: 1 as FlowStep, // Move to step 1 to show chat panel immediately
        chatHistory: pendingMessages, // Show user message immediately
        emailContent: { subject: '', sections: [] },
        activeTab: "Generate",
        showPreview: false,
        inputText: "",
        loading: true, // Show loading spinner in chat panel
        error: null,
        suggestions: [],
        next_action: '',
        is_input_restricted_to_suggestions: false
      };

      updateChatState(immediateState);
      
      // Set API call in progress state
      setIsApiCallInProgress(true);
      
      // Step 1: First initialize chat to get conversation_id
      
      // Create AbortController for initChat
      const initAbortController = new AbortController();
      initChatAbortControllerRef.current = initAbortController;
      
      const selectedOperation = getSelectedOperation();
      const initResponse = await initChat(selectedOperation, initAbortController.signal);
      
      if (!initResponse || !initResponse.conversation_id) {
        throw new Error('Failed to initialize chat - no conversation ID received');
      }
      
      setActiveConversationId(initResponse.conversation_id);
      
      // Create AbortController for startNewChat
      const startNewChatAbortController = new AbortController();
      startNewChatAbortControllerRef.current = startNewChatAbortController;
      
      const response = await startNewChat(operation, initResponse.conversation_id, startNewChatAbortController.signal);
      
      if (!response || !Array.isArray(response.messages) || response.messages.length === 0) {
        handleChatInterruption(pendingMessages, 'No response received while starting the selected operation.');
        return;
      }

      if (response) {
        // Update email content if available
        let updatedEmailContent = { ...chatState.emailContent };
        if (response.email_content_preview && response.email_content_preview.length > 0) {
          const preview = response.email_content_preview[0];
          updatedEmailContent = {
            ...updatedEmailContent,
            subject: preview.subject,
            sections: preview.sections,
          };
        }

        const updatedState: ChatState = {
          step: 1 as FlowStep,
          chatHistory: response.messages,
          emailContent: response.email_content_preview && response.email_content_preview.length > 0 
            ? response.email_content_preview[0] 
            : { subject: '', sections: [] },
          activeTab: "Generate",
          showPreview: false,
          inputText: "",
          loading: false, // Stop loading after API response
          error: null,
          suggestions: response.suggestions || [],
          next_action: response.next_action,
          is_input_restricted_to_suggestions: response.is_input_restricted_to_suggestions || false
        };

        updateChatState(updatedState);
        
        // Save state for this conversation using the init conversation ID
        setChatStates(prev => ({
          ...prev,
          [initResponse.conversation_id]: updatedState
        }));
        
      }
    } catch (error) {
      const contextError = error instanceof Error ? error.message : 'Failed to start operation. Please try again.';
      handleChatInterruption(pendingMessages, contextError);
    } finally {
      // Always clear API call in progress state when operation completes
      setIsApiCallInProgress(false);
    }
  }, [chatState, updateChatState, initChat, startNewChat, handleChatInterruption, getSelectedOperation]);

  // Handle option selection
  const handleOptionSelect = useCallback(async (option: Suggestion) => {
    let updatedMessages: Message[] = [];
    try {
      // Handle special suggestions from email API response
      if (option.id === 'PREVIEW') {
        // PREVIEW suggestion - show email preview modal (no API call)
        console.log('📧 PREVIEW suggestion clicked - opening email preview modal');
        setShowEmailPreviewModal(true);
        return;
      }
      
      if (option.id === 'RESTART') {
        // RESTART suggestion - reset the entire chat
        console.log('🔄 RESTART suggestion clicked - resetting chat');
        resetAllChatStates();
        return;
      }

      // First add user message to chat history immediately
      const userMessage = { role: "user" as const, content: option.text };
      updatedMessages = [...chatState.chatHistory, userMessage];
      
      // Update selected filters
      setSelectedFilters(prev => [...prev, option.text]);
      
      updateChatState({ 
        loading: true,
        chatHistory: updatedMessages // Show user message immediately
      });

      // Set API call in progress state
      setIsApiCallInProgress(true);

      if (currentDealerId && activeConversationId) {
        // Create AbortController for sendChatMessage
        const sendChatAbortController = new AbortController();
        sendChatMessageAbortControllerRef.current = sendChatAbortController;
        
        const response = await sendChatMessage({
          dealer_id: currentDealerId,
          conversation_id: activeConversationId,
          selected_option: option.id,
          messages: updatedMessages, // Use updated messages with user message
        }, sendChatAbortController.signal);

        if (!response || !Array.isArray(response.messages) || response.messages.length === 0) {
          handleChatInterruption(updatedMessages, 'No response received for the selected option.');
          return;
        }

        if (response) {
          const { suggestions, email_content_preview, next_action, messages, is_input_restricted_to_suggestions, terminal_action } = response;
          
          const updatedState: ChatState = {
            step: Math.min(chatState.step + 1, 6) as FlowStep,
            chatHistory: messages, // Use complete messages array from API
            emailContent: email_content_preview && email_content_preview.length > 0 
              ? email_content_preview[0] 
              : chatState.emailContent,
            activeTab: chatState.activeTab,
            showPreview: chatState.showPreview,
            inputText: chatState.inputText,
            loading: false,
            error: null,
            suggestions: suggestions || [], // Add suggestions from API response
            next_action: next_action,
            is_input_restricted_to_suggestions: is_input_restricted_to_suggestions || false,
            terminal_action: terminal_action || false
          };

          updateChatState(updatedState);
          setChatStates(prev => ({
            ...prev,
            [activeConversationId]: { ...prev[activeConversationId], ...updatedState }
          }));

          // Check if email generation is ready and trigger API call
          if (isEmailGenerationReady(messages, next_action, terminal_action)) {
            await generateEmailFromConversation(messages, activeConversationId);
          }
        }
      }
    } catch (error) {
      console.error('Error handling option select:', error);
      const contextError = error instanceof Error ? error.message : 'Failed to process your selection. Please try again.';
      handleChatInterruption(updatedMessages, contextError);
    } finally {
      // Always clear API call in progress state when option selection completes
      setIsApiCallInProgress(false);
    }
  }, [chatState, updateChatState, currentDealerId, activeConversationId, sendChatMessage, isEmailGenerationReady, generateEmailFromConversation, resetAllChatStates, handleChatInterruption]);

  // Handle send message
  const handleSendMessage = useCallback(async (message: string) => {
    let pendingMessages: Message[] = [];
    try {
      // For generate interface, check if message matches an operation
      if (interfaceType === 'generate') {
        const matchedOperation = findOperationByTitle(message);
        if (matchedOperation) {
          // Use the operation's action field
          const operationAction = matchedOperation.action || message;
          
          // First add user message to chat history immediately
          const userMessage = { role: "user" as const, content: message };
          pendingMessages = [...chatState.chatHistory, userMessage];
          
          updateChatState({ 
            loading: true,
            chatHistory: pendingMessages,
            inputText: "",
            step: 1 as FlowStep
          });

          // Set API call in progress state
          setIsApiCallInProgress(true);

          // Initialize chat and start new chat with operation action
          let conversationId = activeConversationId;
          if (!conversationId && currentDealerId) {
            const initAbortController = new AbortController();
            initChatAbortControllerRef.current = initAbortController;
            
            const selectedOperation = getSelectedOperation();
            const initResponse = await initChat(selectedOperation, initAbortController.signal);
            
            if (!initResponse || !initResponse.conversation_id) {
              throw new Error('Failed to initialize chat - no conversation ID received');
            }
            
            conversationId = initResponse.conversation_id;
            setActiveConversationId(conversationId);
          }

          if (currentDealerId && conversationId) {
            const startNewChatAbortController = new AbortController();
            startNewChatAbortControllerRef.current = startNewChatAbortController;
            
            const response = await startNewChat(operationAction, conversationId, startNewChatAbortController.signal);
            
            if (!response || !Array.isArray(response.messages) || response.messages.length === 0) {
              handleChatInterruption(pendingMessages, 'No response received while starting the selected operation.');
              return;
            }

            if (response) {
              const updatedState: ChatState = {
                step: 1 as FlowStep,
                chatHistory: response.messages,
                emailContent: response.email_content_preview && response.email_content_preview.length > 0 
                  ? response.email_content_preview[0] 
                  : { subject: '', sections: [] },
                activeTab: "Generate",
                showPreview: false,
                inputText: "",
                loading: false,
                error: null,
                suggestions: response.suggestions || [],
                next_action: response.next_action,
                is_input_restricted_to_suggestions: response.is_input_restricted_to_suggestions || false
              };

              updateChatState(updatedState);
              setChatStates(prev => ({
                ...prev,
                [conversationId]: updatedState
              }));
            }
          }
          
          return; // Exit early for operation handling
        }
      }

      // Default message handling for non-operation messages or other interface types
      const userMessage = { role: "user" as const, content: message };
      pendingMessages = [...chatState.chatHistory, userMessage];
      
      updateChatState({ 
        loading: true,
        chatHistory: pendingMessages, // Show user message immediately
        inputText: "", // Clear input text immediately
        step: interfaceType === 'homepage' || interfaceType === 'generate' ? 1 as FlowStep : Math.min(chatState.step + 1, 6) as FlowStep // Move to step 1 to show chat panel
      });

      // Set API call in progress state
      setIsApiCallInProgress(true);

      // If no active conversation, initialize chat first
      let conversationId = activeConversationId;
      if (!conversationId && currentDealerId) {
        // Create AbortController for initChat
        const initAbortController = new AbortController();
        initChatAbortControllerRef.current = initAbortController;
        
        const selectedOperation = getSelectedOperation();
        const initResponse = await initChat(selectedOperation, initAbortController.signal);
        
        if (!initResponse || !initResponse.conversation_id) {
          throw new Error('Failed to initialize chat - no conversation ID received');
        }
        
        conversationId = initResponse.conversation_id;
        setActiveConversationId(conversationId);
      }

      if (currentDealerId && conversationId) {
        // Create AbortController for sendChatMessage
        const sendChatAbortController = new AbortController();
        sendChatMessageAbortControllerRef.current = sendChatAbortController;
        
        const response = await sendChatMessage({
          dealer_id: currentDealerId,
          conversation_id: conversationId,
          messages: pendingMessages, // Use updated messages with user message
        }, sendChatAbortController.signal);

        if (!response || !Array.isArray(response.messages) || response.messages.length === 0) {
          handleChatInterruption(pendingMessages, 'No response returned from chat service.');
          return;
        }

        if (response) {
          const { suggestions, email_content_preview, messages, is_input_restricted_to_suggestions, next_action, terminal_action } = response;
          
          // Replace old chat with new messages from API response
          const updatedState: ChatState = {
            step: Math.min(chatState.step + 1, 6) as FlowStep,
            chatHistory: messages, // Use complete messages array from API
            emailContent: email_content_preview && email_content_preview.length > 0 
              ? email_content_preview[0] 
              : chatState.emailContent,
            activeTab: chatState.activeTab,
            showPreview: chatState.showPreview,
            inputText: "", // Keep input text cleared
            loading: false,
            error: null,
            suggestions: suggestions || [], // Add suggestions from API response
            next_action: response.next_action,
            is_input_restricted_to_suggestions: is_input_restricted_to_suggestions || false,
            terminal_action: response.terminal_action || false
          };

          updateChatState(updatedState);
          setChatStates(prev => ({
            ...prev,
            [conversationId]: { ...prev[conversationId], ...updatedState }
          }));

          // Check if email generation is ready and trigger API call
          if (isEmailGenerationReady(messages, next_action, terminal_action)) {
            await generateEmailFromConversation(messages, conversationId);
          }
        }
      }
    } catch (error) {
      const contextError = error instanceof Error ? error.message : 'Failed to send message. Please try again.';
      handleChatInterruption(pendingMessages.length ? pendingMessages : undefined, contextError);
    } finally {
      // Always clear API call in progress state when message sending completes
      setIsApiCallInProgress(false);
    }
  }, [chatState.chatHistory, chatState.step, updateChatState, currentDealerId, activeConversationId, initChat, sendChatMessage, isEmailGenerationReady, generateEmailFromConversation, interfaceType, findOperationByTitle, startNewChat, handleChatInterruption, getSelectedOperation]);

  
  // Chat history handlers
  const handleNewConversation = useCallback(() => {
    // Mark this as intentional unsubscribe (new conversation)
    intentionalUnsubscribeRef.current = true;
    
    // Clear any pending resubscribe attempts
    if (resubscribeTimeoutRef.current) {
      clearTimeout(resubscribeTimeoutRef.current);
      resubscribeTimeoutRef.current = null;
    }
    
    // CRITICAL: Check if email generation is in progress before unsubscribing
    const isEmailGenInProgress = isGeneratingEmailRef.current;
    const emailGenConversationId = emailGenerationConversationIdRef.current;
    
    // Unsubscribe from current conversation before starting new one
    if (socketServiceRef.current && activeConversationId) {
      if (isEmailGenInProgress && emailGenConversationId === activeConversationId) {
        console.log('⚠️ Skipping unsubscribe in handleNewConversation - email generation in progress');
        // Cancel email generation first
        if (emailGenerationAbortControllerRef.current) {
          emailGenerationAbortControllerRef.current.abort();
          emailGenerationAbortControllerRef.current = null;
        }
        // Update refs
        isGeneratingEmailRef.current = false;
        emailGenerationConversationIdRef.current = null;
        // Now safe to unsubscribe
        setTimeout(() => {
          if (socketServiceRef.current && activeConversationId) {
            socketServiceRef.current.unsubscribeFromConversation(activeConversationId);
            setIsSubscribed(false);
          }
        }, 100);
      } else {
        console.log('🔄 Unsubscribing from conversation before starting new one:', activeConversationId);
        socketServiceRef.current.unsubscribeFromConversation(activeConversationId);
        setIsSubscribed(false);
      }
    }
    
    const newId = `conv_${Date.now()}`;
    const newConversation = {
      id: newId,
      title: 'New Chat',
      timestamp: new Date(),
      type: 'general' as const,
    };
    
    setChatHistory(prev => [newConversation, ...prev]);
    setActiveConversationId(newId);
    
    setChatState(initialChatState);
    setChatStates(prev => ({
      ...prev,
      [newId]: initialChatState
    }));
  }, [activeConversationId]);

  const handleConversationSelect = useCallback((id: string) => {
    // Mark this as intentional unsubscribe (switching conversations)
    const currentActiveId = activeConversationIdRef.current;
    if (currentActiveId && currentActiveId !== id) {
      intentionalUnsubscribeRef.current = true;
      
      // Clear any pending resubscribe attempts
      if (resubscribeTimeoutRef.current) {
        clearTimeout(resubscribeTimeoutRef.current);
        resubscribeTimeoutRef.current = null;
      }
    }
    
    // CRITICAL: Check if email generation is in progress for current conversation
    const isEmailGenInProgress = isGeneratingEmailRef.current;
    const emailGenConversationId = emailGenerationConversationIdRef.current;
    
    // Cancel ALL ongoing API calls when switching conversations
    const abortControllers = [
      { ref: emailGenerationAbortControllerRef, name: 'email generation' },
      { ref: initChatAbortControllerRef, name: 'init chat' },
      { ref: startNewChatAbortControllerRef, name: 'start new chat' },
      { ref: sendChatMessageAbortControllerRef, name: 'send chat message' },
      { ref: updateConversationStateAbortControllerRef, name: 'update conversation state' }
    ];
    
    abortControllers.forEach(({ ref, name }) => {
      if (ref.current) {
        ref.current.abort();
        ref.current = null;
      }
    });
    
    // CRITICAL: Update refs after aborting
    isGeneratingEmailRef.current = false;
    emailGenerationConversationIdRef.current = null;
    
    // Clear all streaming and temporary states when switching conversations
    setStreamingData(null);
    setStreamingText('');
    setCurrentSection(null);
    setIsApiCallInProgress(false);
    setSuccessMessage('');
    setIsGeneratingEmail(false);
    setShowEmailGenerationPopup(false);
    setGeneratedEmail(null);
    setShowEditableEmail(false);
    setIsChatInputDisabled(false);
    
    // Only unsubscribe from current conversation if email generation was not in progress
    // OR if we're switching to a different conversation
    if (socketServiceRef.current && currentActiveId && currentActiveId !== id) {
      if (isEmailGenInProgress && emailGenConversationId === currentActiveId) {
        console.log('⚠️ Email generation was in progress, aborting before switching conversations');
        // Small delay to ensure abort completes
        setTimeout(() => {
          if (socketServiceRef.current && currentActiveId) {
            socketServiceRef.current.unsubscribeFromConversation(currentActiveId);
          }
        }, 100);
      } else {
        socketServiceRef.current.unsubscribeFromConversation(currentActiveId);
      }
    }
    
    setActiveConversationId(id);
    const selectedConversation = chatHistory.find(conv => conv.id === id);
    
    if (selectedConversation) {
      const savedState = chatStates[id];
      if (savedState) {
        setChatState(savedState);
      } else {
        setChatState(initialChatState);
      }
    }
  }, [chatHistory, chatStates]);

  const handleDeleteConversation = useCallback((id: string) => {
    // Mark this as intentional unsubscribe (delete conversation)
    if (activeConversationId === id) {
      intentionalUnsubscribeRef.current = true;
      
      // Clear any pending resubscribe attempts
      if (resubscribeTimeoutRef.current) {
        clearTimeout(resubscribeTimeoutRef.current);
        resubscribeTimeoutRef.current = null;
      }
    }
    
    // CRITICAL: Check if email generation is in progress before unsubscribing
    const isEmailGenInProgress = isGeneratingEmailRef.current;
    const emailGenConversationId = emailGenerationConversationIdRef.current;
    
    // Unsubscribe from conversation if it's being deleted
    if (socketServiceRef.current && activeConversationId === id) {
      if (isEmailGenInProgress && emailGenConversationId === id) {
        console.log('⚠️ Skipping unsubscribe in handleDeleteConversation - email generation in progress');
        // Cancel email generation first
        if (emailGenerationAbortControllerRef.current) {
          emailGenerationAbortControllerRef.current.abort();
          emailGenerationAbortControllerRef.current = null;
        }
        // Update refs
        isGeneratingEmailRef.current = false;
        emailGenerationConversationIdRef.current = null;
        // Now safe to unsubscribe
        setTimeout(() => {
          if (socketServiceRef.current && id) {
            socketServiceRef.current.unsubscribeFromConversation(id);
            setIsSubscribed(false);
          }
        }, 100);
      } else {
        socketServiceRef.current.unsubscribeFromConversation(id);
        setIsSubscribed(false);
      }
    }
    
    setChatHistory(prev => prev.filter(conv => conv.id !== id));
    setChatStates(prev => {
      const newStates = { ...prev };
      delete newStates[id];
      return newStates;
    });
    
    if (activeConversationId === id) {
      setActiveConversationId('');
      setChatState(initialChatState);
    }
  }, [activeConversationId]);

  const handleClearHistory = useCallback(() => {
    console.log('🔄 handleClearHistory called - using comprehensive reset function');
    resetAllChatStates();
  }, [resetAllChatStates]);

  // Preview handlers
  const handlePreview = useCallback(() => {
    updateChatState({ showPreview: true });
  }, [updateChatState]);

  const handleClosePreview = useCallback(() => {
    updateChatState({ showPreview: false });
  }, [updateChatState]);

  const handleShowEditableEmail = useCallback(() => {
    setShowEditableEmail(true);
  }, []);

  const handleCloseEditableEmail = useCallback(() => {
    setShowEditableEmail(false);
  }, []);

  const handleEdit = useCallback(() => {
    setSuccessMessage('Edit functionality will be available in the next update.');
  }, []);

  const handleSave = useCallback(() => {
    try {
      setSuccessMessage('Email template saved successfully!');
    } catch (error) {
      const contextError = error instanceof Error ? error.message : 'Failed to save template. Please try again.';
      updateChatState({ error: contextError });
    }
  }, [chatState.emailContent, updateChatState]);

  const handleCloseError = useCallback(() => {
    updateChatState({ error: null });
  }, [updateChatState]);

  // Email preview modal handlers
  const handleOpenEmailPreviewModal = useCallback(() => {
    setShowEmailPreviewModal(true);
  }, []);

  const handleCloseEmailPreviewModal = useCallback(() => {
    setShowEmailPreviewModal(false);
  }, []);

  const handleCloseSuccess = useCallback(() => {
    setSuccessMessage('');
  }, []);

  // Handle copy conversation ID to clipboard
  const handleCopyConversationId = useCallback(async () => {
    if (!activeConversationId) return;
    
    try {
      await navigator.clipboard.writeText(activeConversationId);
      setSuccessMessage(`Conversation ID copied to clipboard: ${activeConversationId}`);
    } catch (error) {
      console.error('Failed to copy conversation ID:', error);
      setError('Failed to copy conversation ID to clipboard');
    }
  }, [activeConversationId, setError]);

  // Callback after template save and PUT API success to enable chat inputs
  const handleTemplateSaveSuccess = useCallback(async (templateId: string, conversationId: string) => {
    try {
      // Create AbortController for updateConversationState
      const updateAbortController = new AbortController();
      updateConversationStateAbortControllerRef.current = updateAbortController;
      
      const response = await updateConversationState({
        dealer_id: currentDealerId,
        conversation_id: conversationId,
        state: {
          generated_email_template_id: templateId
        }
      }, updateAbortController.signal);

      if (response?.success) {
        setIsChatInputDisabled(false);
        // Success message handled by EmailPreviewModal toast
      } else {
        setError('Failed to update conversation state. Please try again.');
      }
    } catch (error) {
      console.error('Error updating conversation state:', error);
      setError('Failed to update conversation state. Please try again.');
    }
  }, [currentDealerId, updateConversationState]);

  // Sync context error with local error state
  useEffect(() => {
    if (error && error !== chatState.error) {
      updateChatState({ error });
    }
  }, [error, chatState.error, updateChatState]);

  // Handle dealer changes
  useEffect(() => {
    if (currentDealerId) {
      // Mark this as intentional unsubscribe (dealer change)
      intentionalUnsubscribeRef.current = true;
      
      // Clear any pending resubscribe attempts
      if (resubscribeTimeoutRef.current) {
        clearTimeout(resubscribeTimeoutRef.current);
        resubscribeTimeoutRef.current = null;
      }
      
      // CRITICAL: Check if email generation is in progress before changing dealer
      const isEmailGenInProgress = isGeneratingEmailRef.current;
      const emailGenConversationId = emailGenerationConversationIdRef.current;
      
      // Cancel email generation if in progress
      if (isEmailGenInProgress && emailGenerationAbortControllerRef.current) {
        console.log('⚠️ Cancelling email generation due to dealer change');
        emailGenerationAbortControllerRef.current.abort();
        emailGenerationAbortControllerRef.current = null;
      }
      
      // Update refs
      isGeneratingEmailRef.current = false;
      emailGenerationConversationIdRef.current = null;
      
      // Wait a bit for abort to complete before resetting states
      const resetStates = () => {
        setChatState(initialChatState);
        setSuccessMessage('');
        setActiveConversationId('');
        setChatStates({});
        setIsGeneratingEmail(false);
        setShowEmailGenerationPopup(false);
        setIsChatInputDisabled(false);
      };
      
      if (isEmailGenInProgress) {
        // Delay state reset to allow abort to complete
        setTimeout(resetStates, 200);
      } else {
        resetStates();
      }
      
      // Reconnect socket service for new dealer
      if (socketServiceRef.current) {
        socketServiceRef.current.disconnect();
        socketServiceRef.current.connect();
        setIsSocketConnected(socketServiceRef.current.isConnected());
      }
    }
  }, [currentDealerId]);

  // Listen for unsubscribe_success events from backend and trigger auto-resubscribe check
  useEffect(() => {
    if (!socketServiceRef.current || !activeConversationId) return;

    const handleUnsubscribeSuccess = (data: any) => {
      // CRITICAL: Check if email generation is in progress before updating subscription status
      const isEmailGenInProgress = isGeneratingEmailRef.current;
      const emailGenConversationId = emailGenerationConversationIdRef.current;
      const latestActiveConversationId = activeConversationIdRef.current;
      
      // Check if this unsubscribe is for our active conversation
      const conversationId = data?.conversation_id || data;
      if (conversationId === latestActiveConversationId) {
          // Only update subscription status if email generation is not in progress
          if (!isEmailGenInProgress || emailGenConversationId !== conversationId) {
            // CRITICAL: If debug panel unsubscribed directly via emit(), the local subscription map
            // still has the subscription. We need to manually remove it so isSubscribedToConversation returns false.
            // Check if subscription still exists in local map and remove it
            if (socketServiceRef.current.isSubscribedToConversation?.(conversationId)) {
              // Manually remove from subscriptions map to sync local state with server state
              if (!intentionalUnsubscribeRef.current) {
                // The subscription exists locally but server unsubscribed us
                // We need to sync the local state with server state
                try {
                  // Use the new removeSubscriptionLocally method if available
                  if (typeof (socketServiceRef.current as any).removeSubscriptionLocally === 'function') {
                    (socketServiceRef.current as any).removeSubscriptionLocally(conversationId);
                  } else {
                    // Fallback: Access subscriptions map directly
                    const subscriptions = (socketServiceRef.current as any).subscriptions;
                    if (subscriptions && subscriptions instanceof Map) {
                      subscriptions.delete(conversationId);
                    }
                  }
                } catch (error) {
                  // Silently handle errors
                }
              }
            }
            
            // Update state immediately
            setIsSubscribed(false);
            
            // If this was not an intentional unsubscribe, trigger auto-resubscribe immediately
            if (!intentionalUnsubscribeRef.current) {
              // Clear any existing timeout
              if (resubscribeTimeoutRef.current) {
                clearTimeout(resubscribeTimeoutRef.current);
              }
              
              // Trigger resubscribe after a short delay
              resubscribeTimeoutRef.current = setTimeout(() => {
                const currentConversationId = activeConversationIdRef.current;
                const isConnected = socketServiceRef.current?.isConnected();
                const isStillNotSubscribed = !socketServiceRef.current?.isSubscribedToConversation?.(currentConversationId);
                
                // Double-check conditions before resubscribing
                if (currentConversationId && 
                    isConnected && 
                    isStillNotSubscribed && 
                    !intentionalUnsubscribeRef.current) {
                  
                  lastResubscribeAttemptRef.current = Date.now();
                  
                  try {
                    socketServiceRef.current.subscribeToConversation(currentConversationId, handleSocketEvent);
                    setIsSubscribed(true);
                  } catch (error) {
                    // Silently handle errors
                  }
                }
                
                resubscribeTimeoutRef.current = null;
              }, 500);
            }
          }
      }
    };

    // Listen for unsubscribe_success events
    socketServiceRef.current.on?.('unsubscribe_success', handleUnsubscribeSuccess);

    return () => {
      socketServiceRef.current?.off?.('unsubscribe_success', handleUnsubscribeSuccess);
    };
  }, [activeConversationId]);

  // Aggressive monitoring: Check every second if subscription is active, auto-resubscribe if missing
  useEffect(() => {
    // Cache event history check to avoid repeated expensive operations
    let lastEventHistoryCheck = 0;
    let cachedUnsubscribeEvents: any[] = [];
    const EVENT_HISTORY_CACHE_DURATION = 2000; // Cache for 2 seconds
    
    const checkAndResubscribe = () => {
      if (!socketServiceRef.current) return;
      
      const connected = socketServiceRef.current.isConnected();
      // Only update state if value actually changed to avoid unnecessary re-renders
      if (connected !== isSocketConnected) {
        setIsSocketConnected(connected);
      }
      
      // Get latest values using refs
      const latestActiveConversationId = activeConversationIdRef.current;
      const isEmailGenInProgress = isGeneratingEmailRef.current;
      const emailGenConversationId = emailGenerationConversationIdRef.current;
      
      // Only check if we have an active conversation and socket is connected
      if (!latestActiveConversationId || !connected) {
        return;
      }
      
      // Check actual subscription status from socket service
      let isActuallySubscribed = socketServiceRef.current.isSubscribedToConversation?.(latestActiveConversationId) || false;
      
      // CRITICAL: Check if unsubscribe event was sent recently (debug panel case)
      // If unsubscribe was sent but local map still has subscription, we need to sync
      let shouldForceUnsubscribe = false;
      
      // Optimize: Only check event history if cache expired or not checked yet
      const now = Date.now();
      if (now - lastEventHistoryCheck > EVENT_HISTORY_CACHE_DURATION || cachedUnsubscribeEvents.length === 0) {
        try {
          const eventHistory = (socketServiceRef.current as any).getEventHistory?.() || [];
          
          // Check last 10 seconds for unsubscribe events for this conversation
          cachedUnsubscribeEvents = eventHistory.filter((evt: any) => {
            if (!evt || !evt.timestamp) return false;
            
            // Handle different timestamp formats
            let eventTime: number;
            if (typeof evt.timestamp === 'string') {
              eventTime = new Date(evt.timestamp).getTime();
            } else if (typeof evt.timestamp === 'number') {
              eventTime = evt.timestamp;
            } else {
              return false;
            }
            
            const timeDiff = now - eventTime;
            if (timeDiff > 10000 || timeDiff < 0) return false; // More than 10 seconds ago or invalid
            
            // Check if event is unsubscribe
            const isUnsubscribeEvent = evt.event === 'emit:unsubscribe' || 
                                       evt.event === 'unsubscribe' ||
                                       (evt.event && evt.event.includes('unsubscribe'));
            
            if (!isUnsubscribeEvent) return false;
            
            // Check if data matches our conversation ID
            const eventData = evt.data;
            const matchesConversation = eventData === latestActiveConversationId || 
                                       (typeof eventData === 'string' && eventData === latestActiveConversationId) ||
                                       (eventData?.conversation_id === latestActiveConversationId);
            
            return matchesConversation;
          });
          
          lastEventHistoryCheck = now;
        } catch (error) {
          // Silently handle errors to avoid performance impact
        }
      }
      
      // Use cached events
      const recentUnsubscribeEvents = cachedUnsubscribeEvents.filter((evt: any) => {
        if (!evt || !evt.timestamp) return false;
        let eventTime: number;
        if (typeof evt.timestamp === 'string') {
          eventTime = new Date(evt.timestamp).getTime();
        } else if (typeof evt.timestamp === 'number') {
          eventTime = evt.timestamp;
        } else {
          return false;
        }
        const timeDiff = now - eventTime;
        return timeDiff <= 10000 && timeDiff >= 0;
      });
      
      if (recentUnsubscribeEvents.length > 0 && isActuallySubscribed) {
        // Unsubscribe was sent but local map still has subscription - sync it
        // Remove from local map to sync with server state
        try {
          if (typeof (socketServiceRef.current as any).removeSubscriptionLocally === 'function') {
            (socketServiceRef.current as any).removeSubscriptionLocally(latestActiveConversationId);
          } else {
            const subscriptions = (socketServiceRef.current as any).subscriptions;
            if (subscriptions && subscriptions instanceof Map) {
              subscriptions.delete(latestActiveConversationId);
            }
          }
          isActuallySubscribed = false; // Update local check
          shouldForceUnsubscribe = true;
        } catch (error) {
          // Silently handle errors
        }
      }
      
      // Update state to reflect actual subscription status - only if changed
      if (isActuallySubscribed !== isSubscribed || shouldForceUnsubscribe) {
        // Don't update to false if email generation is in progress
        if (!isActuallySubscribed && isEmailGenInProgress && emailGenConversationId === latestActiveConversationId) {
          return; // Skip update during email generation
        }
        setIsSubscribed(isActuallySubscribed);
      }
      
      // CRITICAL: If subscription is missing and we have an active conversation, resubscribe immediately
      // This handles ALL cases: debug panel unsubscribe, network issues, server-side unsubscribe, etc.
      if (!isActuallySubscribed && latestActiveConversationId) {
        // Skip resubscribe only if email generation is in progress for this conversation
        if (isEmailGenInProgress && emailGenConversationId === latestActiveConversationId) {
          return; // Don't interrupt email generation
        }
        
        // CRITICAL: Skip if already resubscribing OR if we just resubscribed to this conversation
        if (isResubscribingRef.current || 
            (lastResubscribedConversationIdRef.current === latestActiveConversationId && 
             Date.now() - lastResubscribeAttemptRef.current < 5000)) {
          return; // Silent skip - already handled or recently resubscribed
        }
        
        // Check if we should resubscribe (debounce to avoid rapid attempts)
        const now = Date.now();
        const timeSinceLastAttempt = now - lastResubscribeAttemptRef.current;
        
        // Only resubscribe if last attempt was more than 5 seconds ago (increased to prevent multiple attempts)
        if (timeSinceLastAttempt > 5000) {
          // Clear any existing timeout
          if (resubscribeTimeoutRef.current) {
            clearTimeout(resubscribeTimeoutRef.current);
            resubscribeTimeoutRef.current = null;
          }
          
          // Set resubscribing flag IMMEDIATELY to prevent duplicate attempts
          isResubscribingRef.current = true;
          lastResubscribedConversationIdRef.current = latestActiveConversationId;
          
          // Reset intentional flag if it was set (so we can resubscribe after intentional actions)
          if (intentionalUnsubscribeRef.current) {
            intentionalUnsubscribeRef.current = false;
          }
          
          // Update last attempt time IMMEDIATELY before resubscribe
          lastResubscribeAttemptRef.current = Date.now();
          
          try {
            // CRITICAL: Ensure handleSocketEvent callback is the latest version
            // Re-register the callback to ensure it's properly bound
            const result = socketServiceRef.current.subscribeToConversation(latestActiveConversationId, handleSocketEvent);
            
            // Immediately update state only if changed
            if (!isSubscribed) {
              setIsSubscribed(true);
            }
            
            // CRITICAL: Ensure API call in progress flag is set if there's an active conversation
            // This allows processing_status events to be shown immediately
            // Use functional update to get latest chat state without dependency
            setChatState(prev => {
              const hasMessages = prev.chatHistory.length > 0;
              if (hasMessages && !isApiCallInProgressRef.current) {
                setIsApiCallInProgress(true);
              }
              return prev; // Don't modify state, just check
            });
            
            // Verify subscription immediately
            const verifySubscribed = socketServiceRef.current?.isSubscribedToConversation?.(latestActiveConversationId);
            if (verifySubscribed) {
              // Reset flag immediately if verified
              isResubscribingRef.current = false;
            } else {
              // If not verified immediately, check again after short delay
              setTimeout(() => {
                const verifyAgain = socketServiceRef.current?.isSubscribedToConversation?.(latestActiveConversationId);
                // Reset flag regardless
                isResubscribingRef.current = false;
              }, 500);
            }
          } catch (error) {
            console.error('❌ [AUTO-RESUBSCRIBE] Failed to resubscribe:', error);
            // Reset flag on error
            isResubscribingRef.current = false;
            lastResubscribedConversationIdRef.current = null;
          }
        } else {
          // Too soon since last attempt, skip this check (silent skip)
          return;
        }
      } else if (isActuallySubscribed && latestActiveConversationId) {
        // Subscription is active - ensure state is correct and reset all flags
        // Only update state if changed to avoid unnecessary re-renders
        if (!isSubscribed) {
          setIsSubscribed(true);
        }
        // CRITICAL: Reset all resubscribing flags if subscription is active
        if (isResubscribingRef.current) {
          isResubscribingRef.current = false;
        }
        // Reset last resubscribed conversation ID if subscription is active
        if (lastResubscribedConversationIdRef.current === latestActiveConversationId) {
          // Keep it for 5 seconds to prevent immediate re-resubscribe, then clear
          setTimeout(() => {
            if (socketServiceRef.current?.isSubscribedToConversation?.(latestActiveConversationId)) {
              lastResubscribedConversationIdRef.current = null;
            }
          }, 5000);
        }
      }
    };

    // Check immediately on mount/update
    checkAndResubscribe();
    
    // Then check every second aggressively
    const interval = setInterval(checkAndResubscribe, 1000);
    
    return () => {
      clearInterval(interval);
      // Cleanup resubscribe timeout on unmount
      if (resubscribeTimeoutRef.current) {
        clearTimeout(resubscribeTimeoutRef.current);
        resubscribeTimeoutRef.current = null;
      }
    };
    // Only depend on activeConversationId - other values accessed via refs to avoid unnecessary re-runs
  }, [activeConversationId]);

  // Helper function to determine canvas type
  const getCanvasType = (): CanvasType => {
    return interfaceType === 'generate' ? 'generate' : 'default';
  };

  // Render content based on step and interface type
  const renderContent = () => {
    // Homepage interface - show clean interface initially, then chat interface after message
    if (interfaceType === 'homepage') {
      // If no chat history, show homepage interface
      if (chatState.chatHistory.length === 0) {
        return (
          <Box>
            <Box sx={{ marginTop: '48px', marginBottom: '48px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <PageHeader sx={{ marginBottom: '0 !important', marginLeft: '36px' }}>Home</PageHeader>
              </Box>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              height: 'calc(100vh - 200px)', // Adjust height to account for title
              width: '100%',
              boxSizing: 'border-box',
              overflow: 'hidden',
              justifyContent: 'center', 
              alignItems: 'center',
            }}>
              <CommonInterface
                onSendMessage={handleSendMessage}
                onReset={handleReset}
                loading={chatState.loading}
                title="How can I help you today?"
                suggestions={[
                  'Track campaign performance',
                  'Run ad-hoc campaigns',
                  'Generate emails',
                  'Analyze your data',
                ]}
                interfaceType={interfaceType}
              />
            </Box>
          </Box>
        );
      }
      
      // If chat has started, show only chat interface (no email preview)
      return (
        <Box sx={{ 
          display: 'flex', 
          height: '100vh', // Full viewport height
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <Box sx={{ 
            width: '100%',
            maxWidth: '1200px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <CleanChatPanel
              chatHistory={chatState.chatHistory}
              onOptionSelect={handleOptionSelect}
              onSendMessage={handleSendMessage}
              inputText={chatState.inputText}
              setInputText={(text) => updateChatState({ inputText: text })}
              loading={chatState.loading}
              onReset={handleReset}
              suggestions={chatState.suggestions}
              isInputRestricted={chatState.is_input_restricted_to_suggestions || isChatInputDisabled}
              selectedFilters={selectedFilters}
            />
          </Box>
        </Box>
      );
    }

    // Generate interface - show CommonInterface on step 0, chat interface on other steps
    if (interfaceType === 'generate' && chatState.step === 0) {
      return (
        <Box>
          <Box sx={{ marginTop: '48px', marginBottom: '48px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <PageHeader sx={{ marginBottom: '0 !important' , marginLeft: '36px'}}>Generate Offers</PageHeader>
            </Box>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            height: 'calc(100vh - 200px)', // Adjust height to account for title
            width: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden',
            justifyContent: 'center', 
            alignItems: 'center',
          }}>
            <CommonInterface
              onSendMessage={handleSendMessage}
              onReset={handleReset}
              loading={chatState.loading || operationsLoading}
              title="What offer would you like to generate?"
              suggestions={getOperationsAsSuggestions}
              isLoadingSuggestions={operationsLoading}
            />
          </Box>
        </Box>
      );
    }

    // Analyze interface - show clean interface initially, then chat-only interface after message
    if (interfaceType === 'analyze') {
      // If no chat history, show analyze interface
      if (chatState.chatHistory.length === 0) {
        return (
          <Box>
            <Box sx={{ marginTop: '48px', marginBottom: '48px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <PageHeader sx={{ marginBottom: '0 !important', marginLeft: '36px' }}>Analyze</PageHeader>
              </Box>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              height: 'calc(100vh - 200px)', // Adjust height to account for title
              width: '100%',
              boxSizing: 'border-box',
              overflow: 'hidden',
              justifyContent: 'center', 
              alignItems: 'center',
            }}>
              <CommonInterface
                onSendMessage={handleSendMessage}
                onReset={handleReset}
                loading={chatState.loading}
                title="How can I help you analyze your data?"
                suggestions={[
                  'Analyze campaign performance',
                  'Review customer engagement',
                  'Identify trends in sales',
                  'Evaluate lead quality',
                ]}
              />
            </Box>
          </Box>
        );
      }
      
      // If chat has started, show only chat interface (no email preview)
      return (
        <Box sx={{ 
          display: 'flex', 
          height: '100vh', // Full viewport height
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <Box sx={{ 
            width: '100%',
            maxWidth: '1200px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <CleanChatPanel
              chatHistory={chatState.chatHistory}
              onOptionSelect={handleOptionSelect}
              onSendMessage={handleSendMessage}
              inputText={chatState.inputText}
              setInputText={(text) => updateChatState({ inputText: text })}
              loading={chatState.loading}
              onReset={handleReset}
              suggestions={chatState.suggestions}
              isInputRestricted={chatState.is_input_restricted_to_suggestions || isChatInputDisabled}
              selectedFilters={selectedFilters}
            />
          </Box>
        </Box>
      );
    }

    // Target interface - show clean interface initially, then chat with buyer profile interface after message
    if (interfaceType === 'target') {
      // If no chat history, show target interface
      if (chatState.chatHistory.length === 0) {
        // Get segments as suggestions for target interface
        const segmentsAsOperations = getSegmentsAsOperations();
        const targetSuggestions = segmentsAsOperations.length > 0 
          ? segmentsAsOperations.map(segment => segment.display_text)
          : [
              'Accord Buyers',
              'CR-V Buyers', 
              'HR-V Buyers',
              'Pilot Buyers',
              'Ridgeline Buyers',
              'Specific VIN',
              'Move distressed inventory',
            ];

        return (
          <Box>
            <Box sx={{ marginTop: '48px', marginBottom: '48px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <PageHeader sx={{ marginBottom: '0 !important', marginLeft: '36px' }}>Adhoc Targeting Campaigns</PageHeader>
              </Box>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              height: 'calc(100vh - 200px)', // Adjust height to account for title
              width: '100%',
              boxSizing: 'border-box',
              overflow: 'hidden',
              justifyContent: 'center', 
              alignItems: 'center',
            }}>
              <CommonInterface
                onSendMessage={handleSendMessage}
                onReset={handleReset}
                loading={chatState.loading || operationsLoading}
                title="Who would you like to target?"
                suggestions={targetSuggestions}
              />
            </Box>
          </Box>
        );
      }
      
      // If chat has started, show chat and buyer profile interface
      return (
        <Box sx={{ 
          display: 'flex', 
          height: '100vh', // Full viewport height
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}>
          <SplitPaneMAIQ
              left={
                <CleanChatPanel
                  chatHistory={chatState.chatHistory}
                  onOptionSelect={handleOptionSelect}
                  onSendMessage={handleSendMessage}
                  inputText={chatState.inputText}
                  setInputText={(text) => updateChatState({ inputText: text })}
                  loading={chatState.loading}
                  onReset={handleReset}
                  suggestions={chatState.suggestions}
                  isInputRestricted={chatState.is_input_restricted_to_suggestions || isChatInputDisabled}
                  selectedFilters={selectedFilters}
                />
              }
              right={
                <Canvas 
                  isLoading={chatState.loading}
                  details={currentConversation?.progress_cards || []}
                />
              }
              defaultWidth={70}
              minWidth={10}
              maxWidth={90}
            />
        </Box>
      );
    }

    // Default operations panel for other interface types
    if (chatState.step === 0) {
      return (
        <Box sx={{ 
          display: 'flex', 
          height: '100vh', // Full viewport height
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
          justifyContent: 'center', 
          alignItems: 'center',
        }}>
          <OperationsPanel
            onPromptSelect={handleOperationSelect}
            loading={chatState.loading}
          />
        </Box>
      );
    }

    // Show main chat and preview interface
    return (
      <Box sx={{ 
        display: 'flex', 
        height: '100vh', // Full viewport height
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}>
        <SplitPaneMAIQ
            left={
              <CleanChatPanel
                chatHistory={chatState.chatHistory}
                onOptionSelect={handleOptionSelect}
                onSendMessage={handleSendMessage}
                inputText={chatState.inputText}
                setInputText={(text) => updateChatState({ inputText: text })}
                loading={chatState.loading}
                onReset={handleReset}
                suggestions={chatState.suggestions}
                isInputRestricted={chatState.is_input_restricted_to_suggestions || isChatInputDisabled}
                selectedFilters={selectedFilters}
              />
            }
            right={
              interfaceType === 'generate' ? (
                <>
                  <Canvas 
                    isLoading={chatState.loading}
                    details={currentConversation?.progress_cards || []}
                    isGeneratingEmail={isGeneratingEmail}
                    streamingData={streamingData}
                    currentSection={currentSection}
                    onOpenEmailGenerationPopup={() => setShowEmailGenerationPopup(true)}
                  />
                  {/* Generation Progress Popup for generate interface */}
                  {isGeneratingEmail && (
                    <GenerationProgressPopupMAIQ
                      open={showEmailGenerationPopup}
                      isGenerating={isGeneratingEmail}
                      streamingData={streamingData}
                      streamingText={streamingText}
                      currentSection={currentSection || undefined}
                      sections={streamingData?.message?.sections || []}
                      isFinal={streamingData?.message?.isFinal || false}
                      showProgress={true}
                      message="Generating Email..."
                      onClose={() => setShowEmailGenerationPopup(false)}
                    />
                  )}
                </>
              ) : (
                <EmailContentPreview
                  emailContent={chatState.emailContent}
                  step={chatState.step}
                  onPreview={handlePreview}
                  onEdit={handleEdit}
                  onSave={handleSave}
                  showPreview={chatState.showPreview}
                  onClosePreview={handleClosePreview}
                  onShowEditableEmail={handleShowEditableEmail}
                  showEditableEmail={showEditableEmail}
                  onCloseEditableEmail={handleCloseEditableEmail}
                  onEmailContentChange={(updatedContent) => {
                    setChatState(prev => ({
                      ...prev,
                      emailContent: updatedContent
                    }));
                  }}
                  onGeneratedEmailChange={(updatedEmail) => {
                    setGeneratedEmail({
                      generatedHtml: updatedEmail.html,
                      subject: updatedEmail.subject,
                      conversation_id: updatedEmail.conversation_id,
                      emailPreview: generatedEmail?.emailPreview
                    });
                  }}
                  isGenerating={chatState.loading}
                  isGeneratingEmail={isGeneratingEmail}
                  generatedEmail={generatedEmail ? {
                    html: generatedEmail.generatedHtml,
                    subject: generatedEmail.subject,
                    conversation_id: generatedEmail.conversation_id,
                    emailPreview: generatedEmail.emailPreview
                  } : null}
                  streamingData={streamingData}
                  streamingText={streamingText}
                  currentSection={currentSection}
                  conversationId={activeConversationId}
                  onTemplateSaveSuccess={handleTemplateSaveSuccess}
                />
              )
            }
            defaultWidth={70}
            minWidth={30}
            maxWidth={80}
          />
      </Box>
    );
  };

  return (
    <Box sx={{ 
      height: '100vh', // Full viewport height
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden',
      width: '100%',
      boxSizing: 'border-box',
      position: 'relative'
    }}>
      {/* Socket Status Indicator - Fixed position in bottom-right */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 8,
          right: 8,
          zIndex: 1000,
          display: 'flex',
          gap: 1,
          flexDirection: 'row',
          alignItems: 'center'
        }}
      >
        {/* Connection Status */}
        <Tooltip 
          title={
            isSocketConnected 
              ? 'WebSocket connected' 
              : 'WebSocket disconnected - Real-time updates unavailable'
          }
          arrow
        >
          <Chip
            icon={isSocketConnected ? <SettingsInputComponent /> : <SettingsInputComponentOutlined />}
            label={isSocketConnected ? 'Connected' : 'Disconnected'}
            color={isSocketConnected ? 'success' : 'error'}
            size="small"
            variant="outlined"
            sx={{
              backgroundColor: isSocketConnected 
                ? 'rgba(76, 175, 80, 0.1)' 
                : 'rgba(244, 67, 54, 0.1)',
              borderColor: isSocketConnected ? 'success.main' : 'error.main',
              '& .MuiChip-icon': {
                color: isSocketConnected ? 'success.main' : 'error.main'
              }
            }}
          />
        </Tooltip>

        {/* Subscription Status - Always show, grayed out until subscribed */}
        <Tooltip
          title={
            !activeConversationId ? (
              'No active conversation'
            ) : (
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="body2" component="div">
                  {isSubscribed ? 'Subscribed to conversation:' : 'Not subscribed to conversation:'}
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mt: 0.5, mb: 0.5 }}>
                  {activeConversationId}
                </Typography>
                {!isSubscribed && (
                  <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                    Real-time updates unavailable
                  </Typography>
                )}
                <Typography variant="caption" component="div" sx={{ mt: 0.5, opacity: 0.8 }}>
                  Click to copy conversation ID to clipboard
                </Typography>
              </Box>
            )
          }
          arrow
        >
          <Chip
            icon={isSubscribed ? <RadioButtonChecked /> : <RadioButtonUnchecked />}
            label={isSubscribed ? 'Subscribed' : 'Not Subscribed'}
            color={isSubscribed ? 'primary' : 'default'}
            size="small"
            variant="outlined"
            disabled={!isSubscribed}
            onClick={activeConversationId ? handleCopyConversationId : undefined}
            sx={{
              backgroundColor: isSubscribed
                ? 'rgba(25, 118, 210, 0.1)'
                : 'rgba(158, 158, 158, 0.1)',
              borderColor: isSubscribed ? 'primary.main' : 'rgba(158, 158, 158, 0.5)',
              opacity: isSubscribed ? 1 : 0.5,
              cursor: activeConversationId ? 'pointer' : 'default',
              '&:hover': activeConversationId ? {
                opacity: 0.8,
                backgroundColor: isSubscribed
                  ? 'rgba(25, 118, 210, 0.15)'
                  : 'rgba(158, 158, 158, 0.15)'
              } : {},
              '& .MuiChip-icon': {
                color: isSubscribed ? 'primary.main' : 'rgba(158, 158, 158, 0.7)'
              },
              '&.Mui-disabled': {
                opacity: 0.5,
                cursor: 'default'
              }
            }}
          />
        </Tooltip>
      </Box>

      {renderContent()}

      {/* Error Snackbar */}
      <Snackbar
        open={!!chatState.error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {chatState.error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>


      {/* Email Preview Modal */}
      <EmailPreviewModal
        open={showEmailPreviewModal}
        onClose={handleCloseEmailPreviewModal}
        generatedEmail={generatedEmail}
        onGeneratedEmailChange={(updatedEmail) => {
          setGeneratedEmail({
            generatedHtml: updatedEmail.generatedHtml,
            subject: updatedEmail.subject,
            conversation_id: updatedEmail.conversation_id,
            emailPreview: updatedEmail.emailPreview
          });
        }}
        onSaveTemplateClick={() => {
          // Open the save template modal from EmailContentPreview
          // We need to trigger the save template functionality
          console.log('Save template clicked - opening save template modal');
        }}
        onTemplateSaveSuccess={handleTemplateSaveSuccess}
        conversationId={activeConversationId}
        emailContent={chatState.emailContent}
      />
    </Box>
  );
};

export default MiaChat;
